import React, { useCallback, useEffect, useMemo, useState } from "react";
import "./ErhAlertPopup.css";

export type ErhViewerRole = "school" | "admin" | "doctor";

type Incident = {
  incident_id: string;
  status?: string;
  severity?: string;
  created_at?: string;
  org_code?: string;
  student_pk?: string;
  student_id?: string;
  grade?: string;
  handled_by_role?: string;
  handled_by_id?: string;
};

type Props = {
  role: ErhViewerRole;
  orgCode?: string;
  openRoute: (incidentId: string) => string;
  title?: string;
};

const API_BASE = (process.env.REACT_APP_API_BASE_URL || "http://127.0.0.1:8000").replace(/\/+$/, "");

function getToken(role: ErhViewerRole) {
  const keys = role === "admin"
    ? ["admin_access_token", "admin_token", "access_token", "token"]
    : role === "school"
      ? ["school_admin_token", "school_access_token", "school_token", "access_token", "token"]
      : ["doctor_access_token", "doctor_token", "access_token", "token"];

  for (const store of [sessionStorage, localStorage]) {
    for (const key of keys) {
      const value = store.getItem(key);
      if (value) return value.replace(/^Bearer\s+/i, "").trim();
    }
  }
  return "";
}

export default function ErhAlertPopup({ role, orgCode = "", openRoute, title = "AURA ERH™ Emergency Response Hub" }: Props) {
  const [incidents, setIncidents] = useState<Incident[]>([]);
  const [error, setError] = useState("");
  const [acknowledged, setAcknowledged] = useState<Record<string, boolean>>({});

  const load = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (orgCode) params.set("org_code", orgCode);
      const token = getToken(role);
      const response = await fetch(`${API_BASE}/realtime/sos/active${params.toString() ? `?${params}` : ""}`, {
        cache: "no-store",
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data?.ok === false) throw new Error(data?.detail || `Request failed (${response.status})`);
      setIncidents(Array.isArray(data?.incidents) ? data.incidents : []);
      setError("");
    } catch (err: any) {
      setError(err?.message || "Unable to check AURA ERH incidents.");
    }
  }, [orgCode, role]);

  useEffect(() => {
    void load();
    const timer = window.setInterval(() => void load(), 5000);
    return () => window.clearInterval(timer);
  }, [load]);

  const active = useMemo(
    () => incidents.filter((row) => String(row.status || "open").toLowerCase() !== "closed"),
    [incidents],
  );

  if (!active.length && !error) return null;

  return (
    <section className="erh-alert-shell" role="region" aria-label={title}>
      <header className="erh-alert-header">
        <div>
          <div className="erh-alert-kicker">Emergency Response Notification</div>
          <strong>{title}</strong>
        </div>
        <span>{active.length} active</span>
      </header>

      {error ? <div className="erh-alert-error">{error}</div> : null}

      <div className="erh-alert-list">
        {active.map((incident) => {
          const severity = String(incident.severity || "high").toLowerCase();
          return (
            <article key={incident.incident_id} className={`erh-alert-item ${severity}`}>
              <div className="erh-alert-copy">
                <div className="erh-alert-id">{incident.incident_id}</div>
                <div className="erh-alert-status">
                  {String(incident.status || "open").toUpperCase()} • {severity.toUpperCase()}
                </div>
                <div className="erh-alert-meta">
                  Organization: {incident.org_code || "Pending"}
                  {incident.grade ? ` • Grade ${incident.grade}` : ""}
                  {incident.created_at ? ` • ${new Date(incident.created_at).toLocaleString()}` : ""}
                </div>
                <div className="erh-alert-privacy">Operational information only. No confidential user-advisor chat is displayed.</div>
              </div>

              <div className="erh-alert-actions">
                <button
                  type="button"
                  className="erh-open-button"
                  onClick={() => window.open(openRoute(incident.incident_id), "_blank", "noopener,noreferrer")}
                >
                  Open AURA ERH
                </button>
                <button
                  type="button"
                  className="erh-ack-button"
                  onClick={() => setAcknowledged((prev) => ({ ...prev, [incident.incident_id]: true }))}
                >
                  {acknowledged[incident.incident_id] ? "Acknowledged" : "Acknowledge"}
                </button>
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
}
